import { Button, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useCallback, useMemo } from 'react';
import { api } from '~/trpc-extra/api';
import { useLiveStateManager } from '../useLiveStateManager';
import { useSendVmCommand } from './useSendVmCommand';

export function SaveAiProgressDialog({
  testContentId,
  stepId,
  stepOrder,
  prevOperations,
  previewExecutionId,
}: {
  testContentId: string;
  stepId: string;
  stepOrder: number;
  prevOperations: { id: string; order: number }[];
  previewExecutionId: string;
}) {
  const { vmStatus, updateLiveState } = useLiveStateManager();
  const sendVmCommand = useSendVmCommand();

  const { data: testContent } = api.testContent.get.useQuery(
    { id: testContentId },
    { refetchOnMount: false, refetchOnWindowFocus: false, enabled: !!testContentId },
  );
  const { step, stepIdx } = useMemo(() => {
    const idx = testContent?.steps.findIndex((s) => s.id === stepId);
    const step = idx !== undefined ? testContent?.steps[idx] : undefined;
    return { step, stepIdx: idx };
  }, [testContent, stepId]);
  const trpcUtils = api.useUtils();
  const { mutate: saveAiProgress, isPending: saving } = api.testStep.saveAiProgress.useMutation({
    onSuccess: async () => {
      modals.closeAll();
      await Promise.allSettled([
        trpcUtils.testContent.get.invalidate({ id: testContentId }),
        trpcUtils.testContent.getCode.invalidate({ testContentId }),
      ]);
    },
    onError: (error) => {
      console.error(error);
      notifications.show({
        title: 'Error',
        message: error.message,
        color: 'red',
      });
    },
  });

  const { mutate: resetStepFromPreview } = api.testStep.resetStepFromPreview.useMutation({
    onSuccess: async () => {
      await Promise.allSettled([
        trpcUtils.testContent.get.invalidate({ id: testContentId }),
        trpcUtils.testContent.getCode.invalidate({ testContentId }),
      ]);
      // After discarding AI progress, move the cursor below the AI step if possible
      const nextStep = stepIdx !== undefined && stepIdx !== -1 ? testContent?.steps?.[stepIdx + 1] : undefined;
      const cursor =
        nextStep ?
          { stepId: nextStep.id, stepOrder: nextStep.stepOrder }
        : { stepId, stepOrder, operation: prevOperations.at(-1) };
      updateLiveState((prev) => ({
        ...prev,
        vmStatus: {
          state: 'ready',
          humanState: 'ready',
          aliasesToUrls: vmStatus?.aliasesToUrls,
        },
        recorderCursor: cursor,
      }));
      sendVmCommand({ type: 'set-cursor', ...cursor });
      modals.closeAll();
    },
    onError: (_) => {
      notifications.show({
        title: 'Error',
        message: 'Unable to reset step from preview',
        color: 'red',
      });
    },
  });

  const handleSaveProgress = useCallback(() => {
    if (!testContent) {
      return;
    }
    const executedOperations = testContent.steps.find((s) => s.id === stepId)?.operations;
    if (!executedOperations) {
      return;
    }
    saveAiProgress({
      testContentId,
      stepId,
      previewExecutionId,
    });
    const cursor = { stepId, stepOrder, operation: executedOperations.at(-1) };
    updateLiveState((prev) => ({
      ...prev,
      vmStatus: {
        state: 'ready',
        humanState: 'ready',
        aliasesToUrls: vmStatus?.aliasesToUrls,
      },
      recorderCursor: cursor,
    }));
    sendVmCommand({ type: 'set-cursor', ...cursor });
  }, [
    testContent,
    stepId,
    stepOrder,
    previewExecutionId,
    saveAiProgress,
    updateLiveState,
    vmStatus?.aliasesToUrls,
    sendVmCommand,
  ]);

  return (
    <div className="flex flex-col justify-center gap-3 px-6 pb-2">
      <div className="relative flex items-center justify-center gap-2 dark:border-gray-700">
        <Text className="text-center text-lg font-bold">
          {step?.mode === 'AI_DYNAMIC' ?
            `AI did not finish generating actions for ${stepIdx !== undefined ? `Step ${stepIdx + 1}` : 'this step'}`
          : `AI did not finish auto-healing for ${stepIdx !== undefined ? `step ${stepIdx + 1}` : 'this step'}`}
        </Text>
      </div>

      <div className="text-center text-gray-600 dark:text-gray-400">
        {step?.mode === 'AI_DYNAMIC' ?
          'Would you like to save or discard the partial result?'
        : prevOperations?.length ?? 0 > 0 ?
          'Do you want to keep your previous working version or replace it with the unfinished AI changes?'
        : 'Would you like to save or discard the AI-generated changes?'}
      </div>
      <div className="flex justify-center gap-3 pt-2">
        {/* auto-heal mode */}
        {step?.mode !== 'AI_DYNAMIC' ?
          <>
            <Button
              color={prevOperations?.length ?? 0 > 0 ? 'green' : 'blue'}
              disabled={saving}
              onClick={() => resetStepFromPreview({ stepId, previewExecutionId })}
            >
              {prevOperations?.length ?? 0 > 0 ? 'Keep Previous Version' : 'Discard'}
            </Button>
            <Button
              variant="outline"
              color="red"
              loading={saving}
              onClick={handleSaveProgress}
            >
              {saving ?
                'Saving...'
              : prevOperations?.length ?? 0 > 0 ?
                'Replace'
              : 'Keep Changes'}
            </Button>
          </>
        : <>
            {/* AI generation mode */}
            <Button
              variant="filled"
              color="blue"
              loading={saving}
              onClick={handleSaveProgress}
            >
              {saving ? 'Saving...' : 'Save Progress'}
            </Button>
            <Button
              variant="outline"
              color="red"
              disabled={saving}
              onClick={() => resetStepFromPreview({ stepId, previewExecutionId })}
            >
              Discard
            </Button>
          </>
        }
      </div>
    </div>
  );
}
